import * as Application from "expo-application";
import { ImagePickerAsset } from "expo-image-picker";
import { Api } from "fetch-run";
import { createLogger, error } from "fetch-run/use";
import { Platform } from "react-native";

import { createApiStore } from "./fetch-run-store";
import { Env } from "../env";

//
// Client
//
const appVersion = __DEV__ ? "1.6.0" : Application.nativeApplicationVersion;

const headers = {
  "X-Platform": `rn-${Platform.OS}`,
  "X-AppVersion": appVersion ?? "0.0.0",
  "X-BuildNumber": "1076",
  "X-Version": Env.version,
};

export const api = Api.create(Env.apiUrl, { headers });

if (__DEV__) {
  api.use(createLogger({ verbose: false }));
}

api.use(error);

export const apiStore = createApiStore(api);

//
// Countries
//
export type Country = {
  id: number;
  name: string;
  code: string;
  currency_code: string;
  currency_symbol: string;
  calling_code: string;
  calling_prefix: string;
};

const country = apiStore.route("countries");

export const useCountries = country.list<Country>();

//
// Cities
//
export type CityArea = {
  id: number;
  name: string;
};

export type City = {
  id: number;
  name: string;
  lat: number;
  lng: number;
  country_id: number;
  timezone: string;
  incall: boolean;
  region_id: number;
  radius: number;
  book_buffer: number;
  booking_distance: number;
  tz_offset: number;
  disabled: boolean;
  matured: boolean;
  video_premium: boolean;
  areas: CityArea[];
  currency_code: string;
};

export type UserCity = Omit<City, "areas">;

const city = apiStore.route("cities");

export const useCities = city.list<City>();

//
// Places
//
export type Place = {
  description: string;
  place_id: string;
};

type PlacesSearchReq = {
  q: string;
  uuid: string;
  city_id: number;
};

export function searchPlaces(req: PlacesSearchReq) {
  return api.search<Place[]>("places", req);
}

//
// Locations
//
type LocationStoreReq = {
  place_id: string;
  uuid: string;
};

export function getLocation(id: string) {
  return api.get<Location>(`locations/${id}`);
}

export function storeLocation(url: string, req: LocationStoreReq) {
  return api.post<Location, LocationStoreReq>(url, req);
}

const location = apiStore.route("locations");

export const useLocationStore = location.create<LocationStoreReq, Location>();

type UserLocationUpdateReq = {
  location_id: number;
};

function updateUserLocation(req: UserLocationUpdateReq) {
  return api.put<void, UserLocationUpdateReq>(`me/location`, req);
}

export const useUserLocationUpdate =
  apiStore.createOperation(updateUserLocation);

type UserOneSignalUpdateReq = {
  uuid: string;
};

function updateUserOneSignal(req: UserOneSignalUpdateReq) {
  return api.put<void, UserOneSignalUpdateReq>(`me/onesignal`, req);
}

export const useUserOneSignalUpdate =
  apiStore.createOperation(updateUserOneSignal);

//
// Login
//
export type OtpReq = {
  phone_national: string;
  country_id: number;
  country_code: string;
};

type OtpRes = {
  email: string | null;
  email_verified: boolean;
  phone: string;
};

function requestOtp(req: OtpReq) {
  return api.post<OtpRes, OtpReq>("auth/otp", req);
}

export const useRequestOtp = apiStore.createOperation(requestOtp);

export type LoginReq = {
  phone: string;
  otp: string;
};

type LoginRes = {
  access_token: string;
};

function login(req: LoginReq) {
  return api.post<LoginRes, LoginReq>("auth/login", req);
}

export const useLogin = apiStore.createOperation(login);

//
// Register
//
export type RegisterReq = {
  name: string;
  email: string;
  email_confirmation: string;
  city_id: number | null;
  phone_national: string;
  country_id: number | null;
  country_code: string;
};

type RegisterRes = {
  phone: string;
};

function register(req: RegisterReq) {
  return api.post<RegisterRes, RegisterReq>("auth/register", req);
}

export const useRegister = apiStore.createOperation(register);

//
// User
//
export type BodyType = "slim" | "avg" | "athletic" | "curvy";
export type BustSize = 30 | 32 | 34 | 36 | 38 | 40 | 42 | 44;
export type CupSize = "A" | "B" | "C" | "D" | "D+" | "E";
export type WaistSize = 20 | 22 | 24 | 26 | 28 | 30 | 32 | 34;
export type HipSize = 30 | 32 | 34 | 36 | 38 | 40 | 42 | 44;
export type EndowmentType = "small" | "medium" | "large" | "xlarge" | "post-op";
export type OrientationType = "top" | "bottom" | "versatile";

export type LanguageLevel = "" | "basic" | "avg" | "fluent";
export type Language = {
  code: string;
  level: LanguageLevel;
};

type Option<T> = {
  name: string;
  value: T;
};

export type Image = {
  id: number;
  alt: string | null;
  type: string | null;
  published: boolean;
  verified: boolean;
  banned: boolean;
  path: string;
  url: string;
};

export type ServiceType = "open" | "free" | "multiplier";

export type Service = {
  id: number;
  name: string;
  description: string;
  type: ServiceType;
  paid: boolean;
  paid_type: boolean;
};

export type UserService = Service & {
  pricing: ServiceType | null;
  price: number;
  price_formatted: string;
  value: number;
};

export type Location = {
  id: number;
  name: string;
  place_id: string;
  address: string;
  lat: number;
  lng: number;
  // area: Area | null; TODO
};

export type Rate = {
  amount: number;
  duration: number;
  shots: number;
};

export type UserRate = {
  id: number;
  amount: number;
  duration: number;
  shots: number;
  outcall: boolean;
  auto: boolean;
};

export type CityRate = {
  duration: number;
  shots: number;
  amount_min: number;
  amount_max: number;
};
export type CityRateDicounts = Record<string, number>;

type Social = {
  id: number;
  slug: string;
  name: string;
  public_id?: string;
};

type UserOffer = {
  expires_at: number;
  promotion: {
    name: string;
  };
};

export type User = {
  id: number;
  hash: string;
  email: string | null;
  email_verified: boolean;
  gender_id: number | null;
  name: string | null;
  body_type: BodyType | null;
  height: number | null;
  cup: CupSize | null;
  bust: BustSize | null;
  hip: HipSize | null;
  waist: WaistSize | null;
  endowment?: EndowmentType | null;
  orientation?: OrientationType | null;
  phone: string;
  published: boolean;
  // email_verified: boolean;
  online: boolean;
  online_at: number | null;
  offline_at: number | null;
  payment_method: string | null;
  cancellation_policy: string | null;
  score: number;
  reviews_count: number;
  ratings_count: number;
  languages: Language[];
  rsvp: number;
  identity_required: boolean;
  premium_until: number | null;
  description: string | null;
  description_approved: boolean | null;
  lat: number | null;
  lng: number | null;
  dob: string | null;
  tracking: boolean;
  video_count: number;
  accept_premium_only: boolean;
  independent: boolean;
  premium: boolean;
  offline: boolean;
  image: Image | null;
  images: Image[];
  identity_verified: boolean;
  identity_status: string | null;
  services: UserService[];
  age: 47;
  age_verified: false;
  age_pending: true;
  super: false;
  city: UserCity | null;
  nationality: Country | null;
  brand: null;
  location: Location | null;
  premium_expires_at: number | null;
  premium_tier: number | null;
  areas: CityArea[];
  unread_count: number;
  outcalls: UserRate[];
  incalls: UserRate[];
  bookings_review: [];
  socials: Social[];
  otp: string | null;
  offer: UserOffer | null;
};

export type Config = {
  body_types: Option<BodyType>[];
  cup_sizes: Option<CupSize>[];
  bust_types: Option<BustSize>[];
  waist_sizes: Option<WaistSize>[];
  hip_sizes: Option<HipSize>[];
  languages: Option<string>[];
  language_levels: Option<LanguageLevel>[];
  outcalls: CityRate[];
  incalls?: CityRate[];
  discounts_outcall: CityRateDicounts;
  discounts_incall?: CityRateDicounts;
  services: Service[];
  genders: Option<number>[];
  payment_methods: ["card"];
  socials: Social[];
  cancellations: { name: string; value: string }[];
};

function getConfig() {
  return api.get<Config>("config");
}

export const useConfig = apiStore.createQuery("config", getConfig);

function getUser() {
  return api.get<User>("me");
}

export const useUser = apiStore.createQuery("user", getUser);

export type UserIdentityUpdateReq = {
  cancellation_policy: string | null;
  city_id: number | null;
  dob: number | null;
  description: string | null;
  gender_id: number | null;
  name: string | null;
  nationality_id: number | null;
  payment_method: string | null;
};

function updateUserIdentity(req: UserIdentityUpdateReq) {
  return api.put<void, UserIdentityUpdateReq>("me/identity", req);
}

export const useUserIdentityUpdate =
  apiStore.createOperation(updateUserIdentity);

export type UserBodyUpdateReq = {
  body_type: BodyType;
  height: number; // cm
  bust: BustSize;
  cup: CupSize;
  waist: WaistSize;
  hip: HipSize;
  endowment: EndowmentType;
  orientation: OrientationType;
};

function updateUserBody(req: UserBodyUpdateReq) {
  return api.put<void, UserBodyUpdateReq>("me/body", req);
}

export const useUserBodyUpdate = apiStore.createOperation(updateUserBody);

export type UserLanguagesUpdateReq = {
  languages: Language[];
};

function updateUserLanguages(req: UserLanguagesUpdateReq) {
  return api.put<void, UserLanguagesUpdateReq>("me/languages", req);
}

export const useUserLanguagesUpdate =
  apiStore.createOperation(updateUserLanguages);

export type UserServicesUpdateReq = { id: number; value?: number }[];

function updateUserServices(req: UserServicesUpdateReq) {
  return api.put<void, UserServicesUpdateReq>("me/services", req);
}

export const useUserServicesUpdate =
  apiStore.createOperation(updateUserServices);

export type UserRatesUpdateReq = {
  rates: Rate[];
};

function updateUserOutcallRates(req: UserRatesUpdateReq) {
  return api.put<void, UserRatesUpdateReq>("me/outcalls", req);
}

export const useUserOutcallRatesUpdate = apiStore.createOperation(
  updateUserOutcallRates,
);

function updateUserIncallRates(req: UserRatesUpdateReq) {
  return api.put<void, UserRatesUpdateReq>("me/incalls", req);
}

export const useUserIncallRatesUpdate = apiStore.createOperation(
  updateUserIncallRates,
);

type LaravelFile = {
  uri: string;
  type: string;
  name: string;
};

function getFileName(uri: string) {
  return uri.split("/").pop() || "untitled";
}

function getFileType(fileName: string) {
  const match = /\.(\w+)$/.exec(fileName);

  return match ? `image/${match[1]}` : "image";
}

function storeImage(path: string, image: ImagePickerAsset) {
  const formData = new FormData();

  if (image.file) {
    formData.append("image", image.file);
  } else {
    const fileName = getFileName(image.uri);

    const file: LaravelFile = {
      uri: image.uri,
      type: getFileType(fileName),
      name: fileName,
    };

    formData.append("image", file as unknown as Blob);
  }

  return api.post<void, FormData>(path, formData);
}

function storeUserImage(image: ImagePickerAsset) {
  return storeImage("user/images", image);
}

export const useUserImageStore = apiStore.createOperation(storeUserImage);

function storeUserImageIdentity(image: ImagePickerAsset) {
  return storeImage("user/images/identity", image);
}

export const useUserImageIdentityStore = apiStore.createOperation(
  storeUserImageIdentity,
);

type UserImageDeleteReq = {
  id: number;
};

function deleteUserImage({ id }: UserImageDeleteReq) {
  return api.delete<void>(`me/images/${id}`);
}

export const useUserImageDelete = apiStore.createOperation(deleteUserImage);

type ImageOrder = {
  id: number;
  position: number;
};

type UserImagesOrderUpdateReq = {
  order: ImageOrder[];
};

function updateUserImagesOrder({ order }: UserImagesOrderUpdateReq) {
  return api.put<void, ImageOrder[]>(`me/images`, order);
}

export const useUserImagesOrderUpdate = apiStore.createOperation(
  updateUserImagesOrder,
);

// Videos
export type UserVideo = {
  id: number;
  banned: boolean;
  expired: boolean;
  created_at: number;
  content_id: string;
  processed: boolean;
  published: boolean;
  thumbnail?: string;
  portrait: boolean;
  locked: boolean;
  ratio: number;
  url: string;
  width: number;
  height: number;
  uploadUrl: string;
};

const userVideo = apiStore.route("user/videos");

export const useUserVideos = userVideo.list<UserVideo>();
export const useUserVideoCreate = userVideo.create<void, UserVideo>();
export const useUserVideoDelete = userVideo.delete();

// Statuses
export type UserStatus = {
  id: number;
  text: string;
  image_url: string;
  created_at: string;
  approved: boolean;
};

const userStatus = apiStore.route("user/statuses");

export const useUserStatuses = userStatus.list<UserStatus>();
export const useUserStatusCreate = userStatus.create<FormData, void>();
export const useUserStatusDelete = userStatus.delete();

// Socials
type UserSocialsUpdateReq = {
  socials: Record<number, string | null>;
};

function updateUserSocials(req: UserSocialsUpdateReq) {
  return api.put<void, UserSocialsUpdateReq>("me/socials", req);
}

export const useUserSocialsUpdate = apiStore.createOperation(updateUserSocials);

export type UserCityAreasUpdateReq = {
  areas: number[];
};

function updateUserCityAreas(req: UserCityAreasUpdateReq) {
  return api.put<void, UserCityAreasUpdateReq>("me/areas", req);
}

export const useUserCityAreasUpdate =
  apiStore.createOperation(updateUserCityAreas);

//
// Bookings
//
export type BookingUser = {
  score: number;
  quality: number | null;
  premium: boolean;
  contact_provider: string;
  contact_provider_id: string;
};

export type Booking = {
  accepted?: boolean;
  archived?: boolean;
  cancelled?: boolean;
  completed?: boolean;
  confirmed?: boolean;
  reported?: boolean;
  started?: boolean;
  arrived?: boolean;
  reviewable?: boolean;
  id: number;
  customer_name: string;
  date: string;
  location: Location;
  duration: number;
  total: number;
  isPremium: boolean;
  incall?: boolean;
  phone: string;
  notes: string;
  travel_fee: number;
  travel_fee_formatted: string;
  discount_type: string;
  requested_at: string;
  room_number?: string;
  status_text: string;
  services: UserService[];
  offers: UserService[];
  total_formatted: string;
  user: BookingUser;
  verified?: boolean;
  review?: Review | null;
  city: City;
  timeout: number;
  expired: boolean;
};

const booking = apiStore.route("bookings");

export const useBookings = booking.list<Booking>();

function updateUserOnline() {
  return api.put<void, void>("me/online");
}

export const useUserOnlineUpdate = apiStore.createOperation(updateUserOnline);

function updateUserOffline() {
  return api.put<void, void>("me/offline");
}

export const useUserOfflineUpdate = apiStore.createOperation(updateUserOffline);

// Confirm booking
export function confirmBookingById(id: number) {
  return confirmBooking(`bookings/${id}/confirm`);
}

function confirmBooking(url: string) {
  return api.put<void, void>(url);
}

export const useBookingConfirm = apiStore.createRouteOperation(
  "bookings/:id/confirm",
  confirmBooking,
);

// Cancel booking
type BookingCancelReq = {
  status: string;
};

function cancelBooking(url: string, req: BookingCancelReq) {
  return api.put<void, BookingCancelReq>(url, req);
}
export const useBookingCancel = apiStore.createRouteOperation(
  "bookings/:id/cancel",
  cancelBooking,
);

// Start booking
function startBooking(url: string) {
  return api.put<void, void>(url);
}
export const useBookingStart = apiStore.createRouteOperation(
  "bookings/:id/start",
  startBooking,
);

// Arrive booking
function arriveBooking(url: string) {
  return api.put<void, void>(url);
}
export const useBookingArrive = apiStore.createRouteOperation(
  "bookings/:id/arrive",
  arriveBooking,
);

// Complete booking
function completeBooking(url: string) {
  return api.put<void, void>(url);
}
export const useBookingComplete = apiStore.createRouteOperation(
  "bookings/:id/complete",
  completeBooking,
);

// Report booking
type BookingReportReq = {
  message: string;
};
function reportBooking(url: string, req: BookingReportReq) {
  return api.post<void, BookingReportReq>(url, req);
}
export const useBookingReport = apiStore.createRouteOperation(
  "bookings/:id/report",
  reportBooking,
);

// Review booking
type BookingReviewReq = {
  text: string;
  score: number;
};

function createBookingReview(url: string, req: BookingReviewReq) {
  return api.post<void, BookingReviewReq>(url, req);
}
export const useBookingReviewCreate = apiStore.createRouteOperation(
  "bookings/:id/review",
  createBookingReview,
);

function updateBookingReview(url: string, req: BookingReviewReq) {
  return api.put<void, BookingReviewReq>(url, req);
}
export const useBookingReviewUpdate = apiStore.createRouteOperation(
  "bookings/:id/review",
  updateBookingReview,
);

//
// Reviews
//
export type Review = {
  id: number;
  created_at: string;
  score: number;
  date: string;
  comments: string;
  editable?: boolean;
};

const review = apiStore.route("reviews");

export const useReviews = review.list<Review>();

//
// Chats
//
export type ChatUser = {
  id: number;
  name: string;
  score: number;
};

export type ChatMessage = {
  id: number;
  model_id: number | null;
  read: boolean;
  text: string;
  created_at: number;
};

export type Chat = {
  id: number;
  blocked: boolean;
  user_id: number | null;
  user: ChatUser;
  model_id: number | null;
  read: boolean;
  created_at: number;
  messages: ChatMessage[];
};

const chat = apiStore.route("chats");

export const useChats = chat.list<Chat>();

// Get single chat by ID
export const useChat = chat.read<Chat>();

// Store chat message
type ChatMessageStoreReq = {
  text: string;
};

const chatMessage = apiStore.route("chats/:chatId/messages");

export const useChatMessageStore = chatMessage.create<
  ChatMessageStoreReq,
  void
>();

// Mark chat as read
function readChat(url: string) {
  return api.put<void, void>(url);
}

export const useChatRead = apiStore.createRouteOperation(
  "chats/:chatId/read",
  readChat,
);

// Block chat
function blockChat(url: string) {
  return api.put<void, void>(url);
}

export const useChatBlock = apiStore.createRouteOperation(
  "chats/:chatId/block",
  blockChat,
);

// Unblock chat
function unblockChat(url: string) {
  return api.put<void, void>(url);
}

export const useChatUnblock = apiStore.createRouteOperation(
  "chats/:chatId/unblock",
  unblockChat,
);
